import { createPortal } from 'react-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useChatContext } from '../context/useChatContext.js';
import { InternalChatView } from '../pages/InternalChat.jsx';
import {
  INTERNAL_CHAT_FOCUS_EVENT,
  INTERNAL_CHAT_HEADER_SLOT_ID,
  INTERNAL_CHAT_LAUNCHER_POSITIONS,
  normalizeInternalChatLauncherPosition,
} from '../utils/internalChatLauncher.js';

import {
  INTERNAL_CHAT_UNREAD_UPDATED_EVENT,
  getInternalChatUnreadMapKey,
  getInternalChatUnreadTotal,
  readInternalChatUnreadMap,
} from '../utils/internalChatUnread.js';

import './InternalChatLauncher.css';

const formatUnreadCount = (value) => {
  const normalized = Number(value) || 0;
  if (normalized <= 0) return '';
  if (normalized > 99) return '99+';
  return String(normalized);
};

export default function InternalChatLauncher() {
  const location = useLocation();
  const {
    attendantData,
    canAccessInternalChat,
    companyId,
    normalizedAttendantId,
  } = useChatContext();
  const [panelState, setPanelState] = useState('closed');
  const [focusRequest, setFocusRequest] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const launcherPosition = useMemo(
    () =>
      normalizeInternalChatLauncherPosition(
        attendantData?.internal_chat_launcher_position
      ),
    [attendantData?.internal_chat_launcher_position]
  );
  const [headerMountNode, setHeaderMountNode] = useState(null);

  const isInternalChatRoute = location.pathname.startsWith('/internal-chat');
  const isPanelOpen = panelState === 'open';
  const shouldMountPanel = panelState !== 'closed';
  const isLauncherHidden = launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE;

  useEffect(() => {
    if (!canAccessInternalChat) {
      setPanelState('closed');
      setUnreadCount(0);
    }
  }, [canAccessInternalChat]);

  useEffect(() => {
    if (launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.NONE) {
      setPanelState('closed');
    }
  }, [launcherPosition]);

  useEffect(() => {
    if (isInternalChatRoute) {
      setPanelState('closed');
    }
  }, [isInternalChatRoute]);

  useEffect(() => {
    const refreshUnreadCount = () => {
      if (!companyId || !normalizedAttendantId) {
        setUnreadCount(0);
        return;
      }
      const unreadMap = readInternalChatUnreadMap(companyId, normalizedAttendantId);
      setUnreadCount(getInternalChatUnreadTotal(unreadMap));
    };

    refreshUnreadCount();

    if (typeof window === 'undefined') return undefined;

    const handleUnreadUpdated = (event) => {
      const targetCompanyId = Number(event?.detail?.companyId ?? companyId);
      if (Number.isFinite(targetCompanyId) && targetCompanyId !== Number(companyId)) {
        return;
      }
      const targetAttendantId = Number(
        event?.detail?.attendantId ?? normalizedAttendantId
      );
      if (
        Number.isFinite(targetAttendantId) &&
        targetAttendantId !== Number(normalizedAttendantId)
      ) {
        return;
      }

      if (typeof event?.detail?.totalUnreadCount === 'number') {
        setUnreadCount(event.detail.totalUnreadCount);
        return;
      }

      refreshUnreadCount();
    };

    const handleStorage = (event) => {
      if (
        event?.key &&
        event.key !== getInternalChatUnreadMapKey(companyId, normalizedAttendantId)
      ) {
        return;
      }
      refreshUnreadCount();
    };

    window.addEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(INTERNAL_CHAT_UNREAD_UPDATED_EVENT, handleUnreadUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [companyId, normalizedAttendantId]);

  const handleFocusRequest = useCallback(
    (event) => {
      if (
        !canAccessInternalChat ||
        isInternalChatRoute
      ) {
        return;
      }

      const rawTarget = event?.detail?.attendantId ?? event?.detail?.targetId ?? null;
      const parsedTarget = Number(rawTarget);
      const targetAttendantId =
        Number.isFinite(parsedTarget) && parsedTarget > 0 ? parsedTarget : null;
      const prefill = typeof event?.detail?.prefill === 'string' ? event.detail.prefill : '';

      setFocusRequest({
        targetAttendantId,
        prefill,
        nonce: Date.now(),
      });
      setPanelState('open');
    },
    [canAccessInternalChat, isInternalChatRoute]
  );

  useEffect(() => {
    window.addEventListener(INTERNAL_CHAT_FOCUS_EVENT, handleFocusRequest);
    return () => {
      window.removeEventListener(INTERNAL_CHAT_FOCUS_EVENT, handleFocusRequest);
    };
  }, [handleFocusRequest]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (launcherPosition !== INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER) {
      setHeaderMountNode(null);
      return;
    }

    setHeaderMountNode(document.getElementById(INTERNAL_CHAT_HEADER_SLOT_ID));
  }, [launcherPosition]);

  if (!canAccessInternalChat || isInternalChatRoute) {
    return null;
  }

  if (isLauncherHidden && !shouldMountPanel) {
    return null;
  }

  const prefersHeader = launcherPosition === INTERNAL_CHAT_LAUNCHER_POSITIONS.HEADER;

  if (prefersHeader && !headerMountNode && !shouldMountPanel) {
    return null;
  }

  const shouldRenderInHeader = prefersHeader && Boolean(headerMountNode);
  const shouldRenderLauncherButton = !isLauncherHidden && !isPanelOpen;

  const launcherContent = (
    <div
      className={`internal-chat-launcher ${
        shouldRenderInHeader
          ? 'internal-chat-launcher--header'
          : 'internal-chat-launcher--bottom-left'
      }`}
    >
      {shouldMountPanel && (
        <div
          className={`internal-chat-launcher__panel ${
            shouldRenderInHeader ? 'internal-chat-launcher__panel--header' : ''
          } ${panelState === 'minimized' ? 'is-hidden' : ''}`}
        >
          <InternalChatView
            variant="floating"
            focusRequest={focusRequest}
            onClose={() => {
              setPanelState('closed');
            }}
            onMinimize={() => {
              setPanelState('minimized');
            }}
          />
        </div>
      )}

      {shouldRenderLauncherButton && (
        <button
          type="button"
          className={`internal-chat-launcher__button ${
            shouldRenderInHeader ? 'internal-chat-launcher__button--header' : ''
          }`}
          onClick={() => setPanelState('open')}
          aria-label="Abrir mini chat interno"
          title="Abrir mini chat interno"
        >
          <MessageSquare size={18} aria-hidden="true" />
          <span className="internal-chat-launcher__button-label">Chat interno</span>
          {unreadCount > 0 && (
            <span className="internal-chat-launcher__button-badge" aria-label={`${unreadCount} novas mensagens`}>
              {formatUnreadCount(unreadCount)}
            </span>
          )}
        </button>
      )}
    </div>
  );

  if (shouldRenderInHeader) {
    return createPortal(launcherContent, headerMountNode);
  }

  return launcherContent;
}
